import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../../lib/cn.js';

/** Shared field chrome, so Input, Textarea and SelectTrigger cannot drift. */
export const fieldClassName = cn(
  'w-full rounded-field border border-border-default bg-surface px-3.5 text-sm text-text',
  'transition-colors placeholder:text-text-subtle',
  'hover:border-border-strong',
  'focus-visible:outline-none focus-visible:border-accent focus-visible:ring-4 focus-visible:ring-accent/25',
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:opacity-60',
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus-visible:ring-danger/20',
);

/**
 * Read-only is not disabled: the value still submits and can still be
 * selected and copied, but nothing about it should invite typing.
 *
 * Kept off `fieldClassName` because `:read-only` is not an input-only
 * selector — it matches any element that is not editable, buttons included.
 * On the select trigger, which is a button, it painted every dropdown in the
 * product with the grey fill and muted text meant for a locked-down field.
 */
export const READ_ONLY = 'read-only:cursor-default read-only:bg-surface-sunken read-only:text-text-muted';

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, type = 'text', ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      type={type}
      className={cn(
        fieldClassName,
        READ_ONLY,
        'h-10',
        'file:border-0 file:bg-transparent file:text-sm',
        className,
      )}
      {...props}
    />
  );
});
